// hooks/useResizable.js
import { useEffect, useState, useRef } from 'react';

export default function useResizable(ref, minWidth = 300, minHeight = 200) {
    const [resizing, setResizing] = useState(false);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const startPos = useRef({ x: 0, y: 0 });
    const startSize = useRef({ width: 0, height: 0 });
    const rafId = useRef(null);

    const onMouseDown = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        startPos.current = { x: e.clientX, y: e.clientY };
        startSize.current = { width: rect.width, height: rect.height };
        setResizing(true);
        e.preventDefault();
    };

    useEffect(() => {
        if (!resizing || !ref.current) return;

        const handleMouseMove = (e) => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            rafId.current = requestAnimationFrame(() => {
                if (!ref.current) return;
                const deltaX = e.clientX - startPos.current.x;
                const deltaY = e.clientY - startPos.current.y;
                const newWidth = Math.max(minWidth, startSize.current.width + deltaX);
                const newHeight = Math.max(minHeight, startSize.current.height + deltaY);
                ref.current.style.width = `${newWidth}px`;
                ref.current.style.height = `${newHeight}px`;
            });
        };

        const handleMouseUp = () => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            setResizing(false);
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            setSize({ width: rect.width, height: rect.height });
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = 'none';
        if (ref.current) ref.current.style.transition = 'none'; // Disable transitions during resize

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            if (ref.current) ref.current.style.transition = ''; // Restore transitions
            if (rafId.current) cancelAnimationFrame(rafId.current);
        };
    }, [resizing, ref, minWidth, minHeight]);

    return { size, onMouseDown };
}